import { AlertType, AuditFields, ClientRef, CommonFields, CommonStatus, PartnerRef } from "./common";
import { ISOString } from "./date";
import type { CoverageAnalysis } from "./documentTransformation";

export enum ViewByDocumentType {
  NONE = "none",
  IN_PROCESS = "inProcess",
  NEEDS_REVIEW = "needsReview",
  FAILED_UPLOADS = "failedUploads",
  COMPLETED = "completed",
}

export enum DocumentStatus {
  UPLOADED = "uploaded",
  UPLOAD_FAILED = CommonStatus.UPLOAD_FAILED,
  PROCESSING = CommonStatus.PROCESSING,
  REVIEW_REQUIRED = CommonStatus.REVIEW_REQUIRED,
  APPROVED = CommonStatus.APPROVED,
  REJECTED = "rejected",
  REPROCESS = "reprocess",
  PROCESSING_FAILED = CommonStatus.PROCESSING_FAILED,
  LOADING = "loading",
  LOADED = "loaded",
  LOADING_FAILED = CommonStatus.LOADING_FAILED,
  COMPLETED = CommonStatus.COMPLETED,
  FAILED = CommonStatus.FAILED,
  ARCHIVED = "archived",
  DELETED = "deleted",
}

/**
 * Reason codes a reviewer can give when rejecting a document (#7441). Labels
 * live in `@/constants/documentRejection`. Mirrors `document.RejectionReasonCode`
 * in coronis-apps.
 */
export enum DocumentRejectionReason {
  WRONG_FILE_OR_CLIENT = "wrong_file_or_client",
  EXTRACTION_UNFIXABLE = "extraction_unfixable",
  DUPLICATE = "duplicate",
  OTHER = "other",
}

export enum DocumentUploadStatus {
  PENDING = CommonStatus.PENDING,
  UPLOADING = "uploading",
  SUCCESS = CommonStatus.SUCCESS,
  ERROR = CommonStatus.ERROR,
}

export enum DocumentRecordType {
  STOPLOSS_CLAIMS = "stoploss_claims",
  CLAIMS_ITEMS = "claims_items",
  REIMBURSEMENTS = "reimbursements",
  ENDORSEMENTS = "endorsements",
  CLAIMANTS = "claimants",
  POLICIES = "policies",
  CLIENTS = "clients",
  // Storage-only, user-attached reference files (#6505 / ADR-086). NOT a
  // processing category — the AI pipeline is skipped. Used by attachment
  // uploads so they aren't stamped as 'policies'.
  ATTACHMENT = "attachment",
}

export enum TargetSchema {
  STOPLOSS_CLAIM = "stoploss_claim",
  CLAIM_ITEM = "claim_item",
  CLAIMANT = "claimant",
  CLIENT = "client",
  REIMBURSEMENT = "reimbursement",
  POLICY = "policy",
}

export enum MultiUploadResultStatus {
  FULFILLED = "fulfilled",
  REJECTED = "rejected",
}

export type DocumentType = "source" | "generated" | "template" | "attachment" | "audit" | "signed";

export type ValidationSeverity = AlertType.ERROR | AlertType.WARNING | AlertType.INFO;

export interface ValidationError {
  field?: string;
  message: string;
  severity: ValidationSeverity;
  rule?: string;
}

interface DocumentUploadPayload {
  file: File;
  name: string;
  record_type: DocumentRecordType;
  vendor_id_ref?: string;
  client_id_ref?: string;
  mime_type?: string;
  type?: DocumentType;
  version?: number;
  /** When true, the backend stores the file without triggering AI extraction */
  storage_only?: boolean;
  /**
   * When true, the upload is a multi-client ("book of business") reimbursement
   * register (#5309 / ADR-057): sent as the X-Multi-Client header, uploaded
   * without a single client, routed onto the multi-client load + review path.
   */
  is_multi_client?: boolean;
}

export interface DocumentUploadItem extends DocumentUploadPayload {
  id: string;
  status: DocumentUploadStatus;
  progress: number;
  error: string | null;
}

export interface BatchUploadRequest {
  files: File[];
  fileIds: string[];
}

export interface BatchUploadResponse {
  success: boolean;
  message: string;
  uploadedFiles?: Array<{
    id: string;
    fileName: string;
    fileSize: number;
    uploadTimestamp: string;
  }>;
}

interface ObjectMetadata {
  container_name?: string;
  object_name?: string;
  content_length?: number;
  mime_type?: string;
  source_file_name?: string;
  checksum?: string;
  upload_job_id?: string;
  storage_only?: boolean;
  /**
   * For multi-sheet xlsx uploads, the workbook sheet this document was
   * ingested from. Set when the upload carried an X-Sheet-Name header;
   * absent for single-sheet uploads, non-spreadsheet files, and legacy
   * documents. Display surfaces compose `${name} — ${sheet_name}` when
   * this field is present. ADO #4267 / Decision B (2026-05-05).
   */
  sheet_name?: string;
  /**
   * True for a multi-client ("book of business") carrier reimbursement
   * register (#5309 / ADR-057): the document was uploaded without a single
   * bound client and each row resolves its own client/policy. Drives the
   * multi-client review surface (client → claimant → rows). Set from the
   * X-Multi-Client upload header; absent/false for single-client documents.
   */
  is_multi_client?: boolean;
}

/**
 * One acknowledged check, as stored on the document (#7440). `check` is the
 * rail card's stable key (see utils/reviewChecks.ts); `detail` is the line
 * the card showed at the time; `user_id`/`at` are server-stamped.
 */
export interface ReviewAcknowledgement {
  check: string;
  detail?: string;
  user_id?: string;
  at: ISOString;
}

/**
 * Full document record from the API - includes all fields and embedded processing results
 * Extends CommonFields (id, aegle_id), AuditFields, ClientRef (client_id_ref), PartnerRef (partner_id_ref)
 */
export interface DocumentRead extends CommonFields, AuditFields, ClientRef, PartnerRef {
  name?: string;
  type?: DocumentType;
  status?: DocumentStatus;
  record_type?: DocumentRecordType;
  uploaded_by_user_id_ref?: string;
  uploaded_datetime?: ISOString;
  processed_datetime?: ISOString;
  version?: number;
  file_format_name?: string;
  object_metadata?: ObjectMetadata;
  vendor_id_ref?: string;

  // Coverage details from the latest transformation, added to document read after retrieval
  coverage_percentage?: number | null;
  coverage_analysis?: CoverageAnalysis | null;

  /**
   * Set when status is `rejected` (#7441). `rejection_reason` is a
   * DocumentRejectionReason code for documents rejected after #7441; older
   * rows may carry free text ("reason not provided"). `rejection_note` is the
   * reviewer's optional note for the uploader. Both omitted when never rejected.
   */
  rejection_reason?: DocumentRejectionReason | string | null;
  rejection_note?: string | null;

  /**
   * Checks the reviewer acknowledged in the "Before you approve" rail when
   * they confirmed Approve and load (#7440 AC6/AC7). Stamped server-side with
   * who and when; omitted when the document was never approved with any.
   */
  review_acknowledgements?: ReviewAcknowledgement[] | null;

  // Error details populated when the document is in a failed state
  error_message?: string;
  error_code?: string;
  retry_count?: number;
}

// Legacy type alias for backwards compatibility
/** @deprecated Use DocumentType instead */
export type DocumentClassificationType = DocumentType;

export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

export interface MultiUploadResult {
  fileId: string;
  fileName: string;
  status: MultiUploadResultStatus;
  document?: DocumentRead;
  error?: string;
}

export interface MultiUploadProgress {
  completed: number;
  total: number;
  currentFile: string;
}
